/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';

import { ContentType } from 'client-code/grid-guide';
import { RecordingStatus } from 'client-code/previews';

import { DimensionScaler } from '../../../../core/device/DimensionScaler';
import { OneLineGuideCell } from '../../../../core/entities';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { CursorManager } from '../../../../core/navigation';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import { type RootState, useTivoSelector } from '../../../../core/tivo/tivo-redux-wrapper';
import { RecordingRestriction } from '../../../../core/usecases';
import { RestrictionMessage } from '../../../common';
import { ImageLoader } from '../../../ImageLoader';
import {
    CELLS_TO_HAVE_AHEAD,
    CELLS_TO_HAVE_BEHIND,
    EPISODIC_ASSET_WIDTH,
    MOVIE_ASSET_WIDTH,
} from '../onelineguide.constants';
import { OneLineGuideContentCell } from '../OneLineGuideContentCell';
import { StarRating } from '../StarRating';

import './OneLineGuideContentStrip.scss';

const OneLineGuideContentStrip: FunctionComponent = React.memo(() => {
    const services = new ServicesClientImpl();

    const [hasCellPosterLoadedMap, setHasCellPosterLoadedMap] = useState<{
        [key: string]: boolean;
    }>({});
    const [recordingStatus, setRecordingStatus] = useState('');
    const stripRef = React.useRef<HTMLDivElement>(null);
    const {
        currentSelectedCell,
        selectedChannel,
        areChannelsSelected,
        cellList,
        offerPreview,
        isFutureMode,
        currentSelectedCellStarRating,
        error,
    } = useTivoSelector((state: RootState) => state.oneLineGuide);
    const { value } = useTivoSelector((state: RootState) => state.inputFactory);

    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

    const isCurrentSelectedCellPosterLoaded = currentSelectedCell
        ? hasCellPosterLoadedMap?.[currentSelectedCell.offerId] || false
        : false;

    const currentSelectedCellIndex: number = cellList.indexOf(currentSelectedCell);
    const currentSelectedCellType: String =
        (currentSelectedCell?.contentType ?? '') === 'movie' ? 'type-movie' : 'type-episodic';

    const isCurrentSelectedCellAdultContent =
        (currentSelectedCell?.isAdult && services.shouldHideMetadata()) || false;
    const cellTitle = currentSelectedCell?.title || '';
    const selectedCellTitle = isCurrentSelectedCellAdultContent ? '' : cellTitle;
    const selectedCellNoServiceConnectionTitle = `${Locale.getString(LocaleKeys.GUIDE_SELECT_TO_WATCH_TEXT)} ${selectedChannel?.callSign}`;

    const selectedCellSubTitle = currentSelectedCell ? currentSelectedCell.subtitle : '';
    const selectedCellEpisodeNumber = currentSelectedCell?.episodeNumber;
    const selectedCellSeasonNumber = currentSelectedCell?.seasonNumber;

    const stripClassName = ['strip-foldable', isFutureMode ? 'strip-future' : 'strip-past']
        .filter(Boolean)
        .join(' ');

    let isActive = !areChannelsSelected;
    if (CursorManager.isCursorAppeared()) {
        isActive = value === 'olg-selected-cell';
    }

    const currentAssetClassName = [
        'strip-selected-asset',
        isFutureMode ? 'strip-future' : 'strip-past',
        isActive ? 'selection-active' : 'selection-inactive',
        currentSelectedCellType,
    ]
        .filter(Boolean)
        .join(' ');

    const isEpisode = currentSelectedCell?.contentType === ContentType.Episode;
    const isMovie = currentSelectedCell?.contentType === ContentType.Movie;

    const selectedCellAdditionalInfo = isCurrentSelectedCellAdultContent
        ? ''
        : `
        ${selectedCellSeasonNumber ? `S${selectedCellSeasonNumber}` : ''} 
        ${selectedCellEpisodeNumber ? `E${selectedCellEpisodeNumber}` : ''} 
        ${selectedCellSubTitle ? `‘${selectedCellSubTitle}’` : ''}
    `;

    const futureLowerBound =
        currentSelectedCellIndex - CELLS_TO_HAVE_BEHIND < 0
            ? 0
            : currentSelectedCellIndex - CELLS_TO_HAVE_BEHIND;
    const pastLowerBound =
        currentSelectedCellIndex - CELLS_TO_HAVE_AHEAD < 0
            ? 0
            : currentSelectedCellIndex - CELLS_TO_HAVE_AHEAD;

    const displayedCells = isFutureMode
        ? cellList.slice(futureLowerBound, currentSelectedCellIndex + CELLS_TO_HAVE_AHEAD)
        : cellList.slice(pastLowerBound, currentSelectedCellIndex + CELLS_TO_HAVE_BEHIND);

    const sideScroll = (
        componentReference: HTMLDivElement,
        currentIndex: number,
        endIndex: number,
    ) => {
        const reference = componentReference;

        const cellsToShift = isFutureMode
            ? displayedCells.slice(0, currentIndex)
            : displayedCells.slice(currentIndex + 1, endIndex);

        const movieCellsNumber = cellsToShift.filter(
            (asset) => asset.contentType === 'movie',
        ).length;
        const episodicCellsNumber = cellsToShift.length - movieCellsNumber;

        const newPosition = Math.abs(
            episodicCellsNumber *
                DimensionScaler.getInstance().getScaledWidth(EPISODIC_ASSET_WIDTH) +
                movieCellsNumber * DimensionScaler.getInstance().getScaledWidth(MOVIE_ASSET_WIDTH),
        );

        const leftValue = parseInt(reference.style.left, 10);
        const rightValue = parseInt(reference.style.right, 10);

        if (leftValue === newPosition) return;
        if (rightValue === newPosition) return;

        if (isFutureMode) {
            if (reference.style.right) reference.style.removeProperty('right');
            reference.style.left = `-${newPosition}px`;
        } else {
            if (reference.style.left) reference.style.removeProperty('left');
            reference.style.right = `-${newPosition}px`;
        }
    };

    const handleOnImageLoadedCallback = (offerId: string) => (isImageLoaded: boolean) => {
        setHasCellPosterLoadedMap((prevState) => ({ ...prevState, [offerId]: isImageLoaded }));
    };

    const getRecordingIconStyle = (recordingStatusType: RecordingStatus): string => {
        switch (recordingStatusType) {
            case RecordingStatus.InProgressRecording:
                return 'olg-in-progress-recording';
            case RecordingStatus.ScheduledAsOnePass:
                return 'olg-scheduled-as-onepass';
            case RecordingStatus.ScheduledAsSingleExplicit:
                return 'olg-scheduled-as-single-explicit';
            default:
                return '';
        }
    };

    useEffect(() => {
        setRecordingStatus('');
        if (offerPreview) {
            offerPreview.then((response) => {
                const recordingStatusClass = getRecordingIconStyle(
                    response?.offerPreview?.contentPreview?.recordingStatus,
                );
                setRecordingStatus(recordingStatusClass);
            });
        }
    }, [offerPreview]);

    if (stripRef.current) {
        sideScroll(
            stripRef.current,
            displayedCells.indexOf(currentSelectedCell),
            displayedCells.length,
        );
    }

    if (isNetworkDisconnected) {
        return <div data-testid="olg-strip-container" className="strip-container"></div>;
    }

    return (
        <div data-testid="olg-strip-container" className="strip-container">
            <div className="strip-cover-container">
                <div data-testid="olg-strip-content" className={stripClassName} ref={stripRef}>
                    {displayedCells.map((cell: OneLineGuideCell) => (
                        <OneLineGuideContentCell
                            key={cell.offerId}
                            contentType={cell.contentType as ContentType}
                            title={cell.title as string}
                            isAdult={(cell?.isAdult && services.shouldHideMetadata()) || false}
                        >
                            <RestrictionMessage
                                displayType={RecordingRestriction.PROGRAM_CELL}
                                programCell={cell}
                                channel={selectedChannel}
                            />
                            <ImageLoader
                                handleOnImageLoaded={handleOnImageLoadedCallback(cell.offerId)}
                                src={cell.posterImageUrl}
                            />
                        </OneLineGuideContentCell>
                    ))}
                </div>
            </div>
            <div data-testid="olg-selected-asset" className={currentAssetClassName}>
                <div className="selected-asset-metadata-container">
                    {isMovie && currentSelectedCellStarRating && (
                        <div>
                            <StarRating rating={currentSelectedCellStarRating} />
                            <div className={recordingStatus}></div>
                        </div>
                    )}
                    {!isMovie && (
                        <>
                            {isCurrentSelectedCellPosterLoaded && (
                                <p className="selected-asset-title">{selectedCellTitle}</p>
                            )}
                            {error && (
                                <p className="selected-asset-title">
                                    {selectedCellNoServiceConnectionTitle}
                                </p>
                            )}
                            {isEpisode && (
                                <p className="selected-asset-additional-info">
                                    {selectedCellAdditionalInfo}
                                </p>
                            )}
                            <div className="olg-episode-icons">
                                <div className={recordingStatus}></div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});

export default OneLineGuideContentStrip;
